import { CSSProperties } from "react";
import { Cubit } from "blac-next";
import IconOnDemandVirtualCare from "@9amhealth/shared/icons/IconOnDemandVirtualCare";
import IconPersonalizedCoaching from "@9amhealth/shared/icons/IconPersonalizedCoaching";
import IconHealthMonitoring from "@9amhealth/shared/icons/IconHealthMonitoring";
import IconMealPlanner from "@9amhealth/shared/icons/IconMealPlanner";
import IconMealInsights from "@9amhealth/shared/icons/IconMealInsights";
import IconContentFeed from "@9amhealth/shared/icons/IconContentFeed";
import type { TranslationKey } from "src/types/translationKey";
import { tracker } from "src/state/state";

export type ToolkitItem = {
  titleKey: TranslationKey;
  color: string;
  Icon: React.ComponentType;
  descriptionKey: TranslationKey;
};

type ToolkitState = {
  activeCard: ToolkitItem | null;
  isTransitioning: boolean;
  reservedTopOffset: number;
  transitioningCards: Set<ToolkitItem>;
  userExperienceGroup: string | undefined;
};

class ToolkitBloc extends Cubit<ToolkitState> {
  wrapperRef: HTMLElement | null = null;
  activeCardElement: HTMLElement | null = null;

  constructor() {
    super({
      activeCard: null,
      isTransitioning: false,
      reservedTopOffset: 0,
      transitioningCards: new Set(),
      userExperienceGroup: undefined
    });
  }

  setUserExperienceGroup = (group: string | undefined) => {
    if (this.state.userExperienceGroup !== group) {
      this.cachedCardHtmlElements = null;
      this.patch({ userExperienceGroup: group });
    }
  };

  private get showPersonalizedCoachingCard(): boolean {
    const group = this.state.userExperienceGroup;
    if (!group) {
      return false;
    }
    return group.startsWith("HWJ_") || group.startsWith("EDJ_");
  }

  cachedCardHtmlElements: NodeListOf<HTMLElement> | null = null;
  cardHtmlElements = () => {
    if (this.cachedCardHtmlElements) {
      return this.cachedCardHtmlElements;
    }

    this.cachedCardHtmlElements = this.wrapperRef?.querySelectorAll(
      'div[role="listitem"]'
    ) as NodeListOf<HTMLElement> | null;

    return this.cachedCardHtmlElements;
  };

  setActiveCard = (item: ToolkitItem) => {
    const itemHtmlElement =
      this.cardHtmlElements()?.[
        this.TOOLKIT_ITEMS.findIndex((i) => i === item)
      ] ?? null;

    // If clicking the same card that's open, just close it
    if (this.state.activeCard === item) {
      this.activeCardElement = null;
      this.patch({ activeCard: null });
      this.trackCardClosed(item);
      this.scrollToWrapperIfNeeded();
      return;
    }

    // If another card is open, close it first then open the new one after animation
    if (this.state.activeCard !== null) {
      const previousCard = this.state.activeCard;
      const previousHeight = this.activeCardElement?.offsetHeight ?? 0;

      this.activeCardElement = null;
      this.patch({
        activeCard: null,
        isTransitioning: true,
        reservedTopOffset: previousHeight
      });
      this.trackCardClosed(previousCard);

      // Wait for close animation (0.4s) then open new card
      setTimeout(() => {
        this.activeCardElement = itemHtmlElement;
        this.patch({
          activeCard: item,
          isTransitioning: false,
          reservedTopOffset: 0
        });
        this.trackCardOpened(item);
        this.scrollToWrapperIfNeeded();
      }, 400);
      return;
    }

    // No card is open, open immediately
    this.activeCardElement = itemHtmlElement;
    this.patch({ activeCard: item });
    this.trackCardOpened(item);
    this.scrollToWrapperIfNeeded();
  };

  scrollToWrapperIfNeeded = () => {
    const wrapperRect = this.wrapperRef?.getBoundingClientRect();
    // only scroll if the top of the wrapper is less than 96px from the top of the viewport (only scroll upwards)
    if (wrapperRect && wrapperRect.top < 96) {
      this.wrapperRef?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  trackCardOpened = (item: ToolkitItem) => {
    tracker.track("Toolkit Card Opened", {
      data: { cardTitleKey: item.titleKey }
    });
  };

  trackCardClosed = (item: ToolkitItem) => {
    tracker.track("Toolkit Card Closed", {
      data: { cardTitleKey: item.titleKey }
    });
  };

  get isStackCompact(): boolean {
    return this.state.activeCard !== null || this.state.isTransitioning;
  }

  setWrapperRef = (ref: HTMLElement | null) => {
    this.wrapperRef = ref;
    this.updateWrapperHeight();
  };

  computeItemStyles = (item: ToolkitItem): CSSProperties => {
    if (this.state.activeCard === item) {
      return {
        height: "auto",
        top: 0
      };
    }

    const index = this.TOOLKIT_ITEMS.findIndex((i) => i === item);
    const isAnyActive = this.state.activeCard !== null;
    const { isTransitioning } = this.state;

    let indexForPosition: number;
    let topOffset: number;

    if (isAnyActive) {
      // Normal active card behavior: cards after active use adjusted index
      const activeIndex = this.TOOLKIT_ITEMS.findIndex(
        (i) => i === this.state.activeCard
      );
      indexForPosition = index > activeIndex ? index - 1 : index;
      topOffset = this.activeCardElement?.offsetHeight ?? 0;
    } else if (isTransitioning) {
      // During transition: all cards use raw index (shift down to make room for closing card)
      indexForPosition = index;
      topOffset = this.state.reservedTopOffset;
    } else {
      // No active card, no transition: all cards stacked normally
      indexForPosition = index;
      topOffset = 0;
    }

    this.updateWrapperHeight();

    const compactOffset = this.isStackCompact ? indexForPosition * 0.5 : 0;

    return {
      top: `calc((${indexForPosition} * 3.6em) + ${topOffset}px + 1.5em - ${compactOffset}em)`
    };
  };

  updateWrapperHeight = () => {
    if (!this.wrapperRef) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.wrapperRef) {
        return;
      }

      const cards = this.cardHtmlElements();
      if (!cards || cards.length === 0) {
        return;
      }

      const numCards = cards.length;

      // Get card height from aspect-ratio (last card is fully visible)
      const cardHeight = cards[numCards - 1]?.offsetHeight ?? 0;

      // Get font-size (card-scale) for em calculations
      const emSize = parseFloat(getComputedStyle(this.wrapperRef).fontSize);

      // Calculate height as if first card is active (worst case):
      // - Active card at top: 0, height: cardHeight
      // - Inactive cards (n-1) stacked below starting at cardHeight + 1.5em
      // - Last inactive at: cardHeight + 1.5em + (n-2) * 3.6em
      // - Total: 2 * cardHeight + (1.5 + (n-2) * 3.6)em
      const stackingHeightEm = 1.5 + (numCards - 2) * 3.6;
      const totalHeight = 2 * cardHeight + stackingHeightEm * emSize;

      this.wrapperRef.style.height = `${totalHeight}px`;
    });
  };

  private static readonly ALL_TOOLKIT_ITEMS: ToolkitItem[] = [
    {
      titleKey: "toolkit.card.onDemandVirtualCare.title",
      color: "#A6C6FF",
      Icon: IconOnDemandVirtualCare,
      descriptionKey: "toolkit.card.onDemandVirtualCare.description"
    },
    {
      titleKey: "toolkit.card.personalizedCoaching.title",
      color: "#C4C9F7",
      Icon: IconPersonalizedCoaching,
      descriptionKey: "toolkit.card.personalizedCoaching.description"
    },
    {
      titleKey: "toolkit.card.healthMonitoring.title",
      color: "#E7CEED",
      Icon: IconHealthMonitoring,
      descriptionKey: "toolkit.card.healthMonitoring.description"
    },
    {
      titleKey: "toolkit.card.mealPlanner.title",
      color: "#FBD1DB",
      Icon: IconMealPlanner,
      descriptionKey: "toolkit.card.mealPlanner.description"
    },
    {
      titleKey: "toolkit.card.mealInsights.title",
      color: "#FDD0BE",
      Icon: IconMealInsights,
      descriptionKey: "toolkit.card.mealInsights.description"
    },
    {
      titleKey: "toolkit.card.contentFeed.title",
      color: "#FED1A1",
      Icon: IconContentFeed,
      descriptionKey: "toolkit.card.contentFeed.description"
    }
  ];

  get TOOLKIT_ITEMS(): ToolkitItem[] {
    if (this.showPersonalizedCoachingCard) {
      return ToolkitBloc.ALL_TOOLKIT_ITEMS;
    }
    return ToolkitBloc.ALL_TOOLKIT_ITEMS.filter(
      (item) => item.titleKey !== "toolkit.card.personalizedCoaching.title"
    );
  }
}

export default ToolkitBloc;
